/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    ArrayExtensions,
    FinitePromiseQueue,
    FinitePromiseQueueError,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getProductAvailabilitiesForSelectedVariant,
    getScanResult,
    GetScanResultInput,
    getSelectedVariant,
    IProductInventoryInformation,
    IPromiseQueue,
    ObjectExtensions,
    ProductAvailabilitiesForSelectedVariantInput,
    QueryResultSettingsProxy,
    SelectedVariantInput,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    updateMaxQuantityForCartLineItem
} from '@msdyn365-commerce-modules/utilities';
import { ProductDimensionFull } from '@msdyn365-commerce/commerce-entities';
import {
    getProductConfigure,
    IErrorState,
    IProductDimensionCallbacks,
    IProductDimensionConfigureResources,
    IProductDimensionConfigureViewProps
} from '@msdyn365-commerce/components';
import { CartLine, ProductDimension, ProductDimensionValue, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { computed, observable } from 'mobx';
import * as React from 'react';

import { ICipQuickOrderData } from './cip-quick-order.data';
import { ICipQuickOrderProps, ICipQuickOrderResources } from './cip-quick-order.props.autogenerated';
import { QuickOrderDrawer, QuickOrderFormComponent, QuickOrderProduct } from './components';
import { QuickOrderTableComponent } from './components/cip-quick-order-table/cip-quick-order-table-component';

/**
 * Quick Order View Props Interface properties.
 */
export interface IQuickOrderViewProps extends ICipQuickOrderProps<ICipQuickOrderData> {
    isB2B?: boolean;
    productText: string;
    isQuickOrderFormExpanded: boolean;
    contentProps: INodeProps;
    drawer: INodeProps;
    quickOrderForm: INodeProps;
    quickOrderFormProductContent: INodeProps;
    quickOrderFormMsgContent: INodeProps;
    quickOrderProps: IModuleProps;
    quickOrderList?: React.ReactNode;
    form: React.ReactNode;
    configure?: IProductDimensionConfigureViewProps;
    isMobileView: boolean;
}

/**
 * Quick Order State Message Interface properties.
 */
export interface IQuickOrderStateMessage {
    isOpen: boolean;
    messageGlyph: string;
    messageText: string;
    statusCssString: string;
}

/**
 * GridSize Interface properties.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Quick Order Interface properties.
 */
export interface IQuickOrderState {
    isQuickOrderFormExpanded: boolean;
    isUpdatingDimension?: boolean;
    productAvailableQuantity: IProductInventoryInformation | undefined;
    productResponse: SimpleProduct | undefined;
    productDimensions: ProductDimensionFull[] | undefined;
    productText: string;
    quantity: number;
    stateMessage?: IQuickOrderStateMessage;
    productDimensionErrorState: IErrorState;
    isMobile: boolean;
    products: SimpleProduct[];
}

/**
 *
 * QuickOrder component.
 * @extends {React.PureComponent<ICipQuickOrderProps<ICipQuickOrderData>, IQuickOrderState>}
 */
class QuickOrder extends React.PureComponent<ICipQuickOrderProps<ICipQuickOrderData>, IQuickOrderState> {
    @observable
    private readonly viewport: GridSize;

    private readonly productTextInput: React.RefObject<HTMLInputElement>;

    private readonly quantityInput: React.RefObject<HTMLInputElement>;

    private readonly waitTime: number = 500;

    private readonly quickOrderFormClass: string;

    private readonly quickOrderFormProductContent: string;

    private readonly quickOrderFormMsgContent: string;

    private readonly telemetryContent: ITelemetryContent;

    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private dimensions: { [id: number]: string } = {};

    private initializedDimensions: Set<string> = new Set();

    private readonly cssClassNameConfigure: string = 'ms-quick-order__content-form-main';

    // @ts-expect-error -- Existing code.
    private cancellableDebounce: debounce;

    private readonly quickOrderCallbacks: IProductDimensionCallbacks = {
        initializeDimension: (initializedDimension: string): void => {
            if (initializedDimension) {
                this.initializedDimensions.add(initializedDimension);
            }
        },
        /**
         * Get Dropdown name.
         * @param selectedDimensionId - Dimension type.
         * @param selectedDimensionValueId - Resources.
         * @returns Dropdown name.
         */
        /* eslint-disable @typescript-eslint/no-explicit-any */
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            // eslint-disable-next-line security/detect-object-injection
            this.dimensions[selectedDimensionId] = selectedDimensionValueId;
            // Only trigger _updateDimensions when all dimensions are initialized
            if (this.initializedDimensions.size === this.state.productDimensions?.length) {
                return this.dimensionUpdateQueue
                    .enqueue(async () => {
                        return this._updateDimensions();
                    })
                    .catch((error: any) => {
                        // Ignore discarded processes.
                        if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                            throw error;
                        }
                    });
            }
        },

        /**
         * Get Dropdown name.
         * @param dimensionType - Dimension type.
         * @param resources - Resources.
         * @returns Dropdown name.
         */
        getDropdownName: (dimensionType: number, resources: IProductDimensionConfigureResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },

        /**
         * Method to call on dimension change.
         * @param isUpdatingDimension - Flag to update dimension.
         */
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        }
    };

    public constructor(props: ICipQuickOrderProps<ICipQuickOrderData>) {
        super(props);
        this.productTextInput = React.createRef();
        this.quantityInput = React.createRef<HTMLInputElement>();
        this.viewport = props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';

        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.state = {
            productText: '',
            isQuickOrderFormExpanded: false,
            productAvailableQuantity: undefined,
            quantity: 1,
            productResponse: undefined,
            productDimensions: undefined,
            productDimensionErrorState: {
                configureErrors: {}
            },
            isMobile: this.isMobile,
            products: []
        };
        this._handleInputChange = this._handleInputChange.bind(this);
        this._handleQuantityChange = this._handleQuantityChange.bind(this);
        this.quickOrderFormClass = 'ms-quick-order__content-form';
        this.quickOrderFormProductContent = 'ms-quick-order__content-form-main';
        this.quickOrderFormMsgContent = 'ms-quick-order__content-form-msg';
    }

    @computed get isMobile(): boolean {
        return this.viewport === 'xs' || this.viewport === 'sm';
    }

    public render(): JSX.Element | null {
        // const { data } = this.props;

        // if (!data.customerInformation.result) {
        //     return null;
        // }

        const { className = '', maxQuantity } = this.props.config;
        const customerInfo = this.props.data.customerInformation.result;
        const {
            quickOrderModuleHeading,
            quickOrderModuleDescription,
            quickOrderAddtobag,
            quickOrderProductQuantityInputLabel,
            quickOrderProductInputLabel,
            quickOrderProductDimensionTypeColor,
            quickOrderProductDimensionTypeConfiguration,
            quickOrderSelectDimensionFormatString,
            quickOrderProductDimensionTypeSize,
            quickOrderProductDimensionTypeStyle,
            quickOrderProductDimensionTypeAmount,
            quickAddQuantityPlaceholderText,
            minQuantityText,
            maxQuantityText
        } = this.props.resources;

        const productConfigureResources: IProductDimensionConfigureResources = {
            selectDimensionFormatString: quickOrderSelectDimensionFormatString,
            productDimensionTypeColor: quickOrderProductDimensionTypeColor,
            productDimensionTypeConfiguration: quickOrderProductDimensionTypeConfiguration,
            productDimensionTypeSize: quickOrderProductDimensionTypeSize,
            productDimensionTypeStyle: quickOrderProductDimensionTypeStyle,
            productDimensionTypeAmount: quickOrderProductDimensionTypeAmount
        };

        const { isQuickOrderFormExpanded, productText, productDimensionErrorState, productResponse, productDimensions } = this.state;

        const moduleClassName: string = 'ms-quick-order';

        const viewProps: IQuickOrderViewProps = {
            ...(this.props as ICipQuickOrderProps<ICipQuickOrderData>),

            quickOrderProps: { className: classnames('ms-quick-order', className), moduleProps: this.props },
            contentProps: { className: 'ms-quick-order__content' },
            isB2B: customerInfo?.IsB2b,
            drawer: {
                tag: QuickOrderDrawer,
                className: `${moduleClassName}__drawer`,
                collapseProps: { isOpen: this.state.isQuickOrderFormExpanded },
                moduleHeadingText: quickOrderModuleHeading,
                moduleDescriptionText: quickOrderModuleDescription,
                requestContext: this.props.context.request,
                telemetryContent: this.telemetryContent
            },
            quickOrderForm: {
                className: `${this.quickOrderFormClass} ${isQuickOrderFormExpanded ? 'qo-show' : 'qo-hide'}`
            },
            quickOrderFormProductContent: {
                className: this.quickOrderFormProductContent
            },
            quickOrderFormMsgContent: {
                className: this.quickOrderFormMsgContent
            },
            productText,
            quickOrderList: ArrayExtensions.hasElements(this.state.products) ? this._renderQuickOrderTable('ms-quick-order') : undefined,
            form: QuickOrderFormComponent({
                productTextInput: this.productTextInput,
                quantityTextInput: this.quantityInput,
                disableAddtoCart: !productResponse,
                ariaLabelAddtoCart: quickOrderAddtobag,
                ariaLabelProduct: quickOrderProductInputLabel,
                ariaLabelQuantity: quickOrderProductQuantityInputLabel,
                addQuantityPlaceholderText: quickAddQuantityPlaceholderText,
                maxChars: 50,
                maxQuantity:
                    productResponse?.Behavior?.MaximumQuantity && productResponse.Behavior.MaximumQuantity > 0
                        ? productResponse.Behavior.MaximumQuantity
                        : maxQuantity,
                quickOrderState: this.state,
                handleInputChange: this._handleInputChange,
                handleAddToCartClick: this._addItemToCart,
                handleQuantityChange: this._handleQuantityChange,
                productBehavior: productResponse?.Behavior,
                minQuantityText,
                maxQuantityText
            }),
            configure:
                productResponse && productDimensions
                    ? getProductConfigure(
                          this.cssClassNameConfigure,
                          this.props.context,
                          this.props.friendlyName,
                          this.props.telemetry,
                          quickOrderSelectDimensionFormatString,
                          productConfigureResources,
                          this.quickOrderCallbacks,
                          productResponse,
                          productDimensions,
                          productDimensionErrorState
                      )
                    : undefined,
            isQuickOrderFormExpanded: this.state.isQuickOrderFormExpanded,
            isMobileView: this.state.isMobile
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Initial the products on quick order.
     * @returns List of quick order products or undefined.
     */
    private _initQuickOrderProducts(): QuickOrderProduct[] {
        const {
            data: {
                cart: { result: cart }
            }
        } = this.props;

        const products: SimpleProduct[] = this.state.products;

        const quickOrderProducts = cart?.cart.CartLines?.map((cartLine: CartLine) => {
            const quickOrderProduct = products.find(product => product.RecordId === cartLine.ProductId);
            if (ObjectExtensions.isNullOrUndefined(quickOrderProduct)) {
                return undefined;
            }

            return new QuickOrderProduct(cartLine, quickOrderProduct);
        });

        return ArrayExtensions.validValues(quickOrderProducts);
    }

    /**
     * Render Quick Order Table.
     * @param className - Class name.
     * @returns JSX element.
     */
    private _renderQuickOrderTable(className: string): JSX.Element {
        const tableClassName = `${className}__table-list`;

        return (
            <QuickOrderTableComponent
                id={tableClassName}
                typeName={this.props.typeName}
                key={tableClassName}
                className={tableClassName}
                data={{ quickOrderProducts: this._initQuickOrderProducts() }}
                context={this.props.context}
                resources={this.props.resources}
                isMobileView={this.state.isMobile}
            />
        );
    }

    /**
     * Method to handle input change.
     * @param query - Class name.
     */
    private readonly _handleInputChange = (query: string): void => {
        if (!query || query.length > 4) {
            this.setState({ productText: query, quantity: 0, productResponse: undefined, productDimensions: [] });
            this.setState({
                stateMessage: {
                    messageText: '',
                    statusCssString: '',
                    messageGlyph: '',
                    isOpen: false
                }
            });
            const inputQuantityText = this.quantityInput.current instanceof HTMLInputElement && this.quantityInput.current;
            if (inputQuantityText) {
                inputQuantityText.value = '0';
            }
        } else {
            return;
        }

        if (this.cancellableDebounce) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- existing code.
            this.cancellableDebounce.cancel();
        }

        const { telemetry, context } = this.props;
        const maxChar = 50;
        if (context) {
            if (!StringExtensions.isNullOrWhitespace(query)) {
                if (query.length > maxChar) {
                    return;
                }

                this.cancellableDebounce = debounce(() => {
                    getScanResult(
                        new GetScanResultInput(
                            context.actionContext.requestContext.apiSettings,
                            query,
                            context.actionContext.requestContext.apiSettings.channelId
                        ),
                        this.props.context.actionContext
                    )
                        .then(results => {
                            this.setState({
                                productResponse: results?.Product,
                                productDimensions: results?.ProductDimensionFull,
                                isQuickOrderFormExpanded: true
                            });
                        })
                        .catch(error => {
                            this.setState({ productText: '' });
                            telemetry.exception(error);
                            telemetry.debug('Unable to get scan results');
                        });
                }, this.waitTime);

                setTimeout(() => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Existing code.
                    this.cancellableDebounce();
                }, 0);
                this.setState({ productText: query, isQuickOrderFormExpanded: true });
            } else {
                this.setState({ productText: '', productResponse: undefined, isQuickOrderFormExpanded: true });
            }
        }
    };

    /**
     * Method to add product in cart.
     */
    // eslint-disable-next-line complexity
    private readonly _addItemToCart = async (): Promise<void> => {
        const { context, data, resources, telemetry } = this.props;
        const { cart } = data;
        const { quickOrderAddedToCartSuccessMessage, quickOrderAddedToCartFailureMessage } = resources;
        const { productResponse, quantity } = this.state;
        this.setState({
            stateMessage: {
                messageText: '',
                statusCssString: '',
                messageGlyph: '',
                isOpen: false
            }
        });

        const isValidCart = productResponse && (await this._validateCart(productResponse, quantity));
        if (isValidCart && cart.result && productResponse !== undefined) {
            // Get product delivery mode info
            const deliveryOptions = await getDeliveryOptionsAsync(
                {
                    callerContext: this.props.context.actionContext,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(this.props.context.actionContext)
                },
                [productResponse.RecordId]
            );
            const emailDeliveryModeCode = this.props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode;
            const defaultDeliveryOption =
                ArrayExtensions.hasElements(deliveryOptions) && ArrayExtensions.hasElements(deliveryOptions[0].DeliveryOptions)
                    ? deliveryOptions[0].DeliveryOptions[0].Code
                    : undefined;

            if (this.state.productAvailableQuantity === undefined) {
                await this._updateAvailability();
            }
            const shouldValidateQuantityLimits: boolean = this._shouldValidateQuantityLimits();
            const { productAvailableQuantity } = this.state;
            const addProductToCartInput = {
                product: productResponse,
                count: quantity,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Existing code.
                enableStockCheck: context.app.config.enableStockCheck,
                availableQuantity: productAvailableQuantity ? productAvailableQuantity.ProductAvailableQuantity.AvailableQuantity : 0,
                additionalProperties: { orderQuantityLimitsFeatureIsEnabled: shouldValidateQuantityLimits },
                isAddEmailDeliveryItemToCart: defaultDeliveryOption !== undefined && defaultDeliveryOption === emailDeliveryModeCode,
                deliveryMode: defaultDeliveryOption
            };

            cart.result
                .addProductToCart(addProductToCartInput)
                .then(async result => {
                    if (result.status === 'SUCCESS') {
                        this.props.telemetry.debug('[quick-order] Cart Updated');
                        const inputProductText = this.productTextInput.current instanceof HTMLInputElement && this.productTextInput.current;
                        const inputQuantityText = this.quantityInput.current instanceof HTMLInputElement && this.quantityInput.current;
                        if (inputProductText) {
                            inputProductText.value = '';
                        }
                        if (inputQuantityText) {
                            inputQuantityText.value = '0';
                        }
                        // clean dimensions once product is added to cart
                        this.dimensions = [];
                        this.setState({
                            productText: '',
                            quantity: 0,
                            productResponse: undefined,
                            productDimensions: [],
                            stateMessage: {
                                messageText: quickOrderAddedToCartSuccessMessage,
                                statusCssString: 'success',
                                messageGlyph: 'fas fa-check',
                                isOpen: true
                            }
                        });
                    } else if (result.substatus === 'MAXQUANTITY') {
                        this.props.telemetry.debug('[quick-order] Unable to Update Cart Line from the quick order', quantity);
                        const maxQtyLimitReachedMessage = this._getMaxQtyMessage(productAvailableQuantity, quantity);

                        this.setState({
                            stateMessage: {
                                messageText: maxQtyLimitReachedMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            }
                        });
                    } else if (result.substatus === 'QUANTITYLIMITS' && shouldValidateQuantityLimits) {
                        const maxQtyLimitReachedMessage = result.errorDetails?.LocalizedMessage!;
                        this.props.telemetry.debug(
                            '[quick-order] Unable to Update Cart Line from the quick order',
                            maxQtyLimitReachedMessage
                        );

                        this.setState({
                            stateMessage: {
                                messageText: maxQtyLimitReachedMessage || quickOrderAddedToCartFailureMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            }
                        });
                    } else {
                        this.props.telemetry.debug('[quick-order] Unable to Update Cart Line from the quick order');

                        this.setState({
                            stateMessage: {
                                messageText: quickOrderAddedToCartFailureMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            }
                        });
                    }
                })
                .catch(error => {
                    telemetry.warning(error);
                    telemetry.debug('[quick-order] Error trying to Update Cart Line from the quick order');

                    this.setState({
                        stateMessage: {
                            messageText: quickOrderAddedToCartFailureMessage,
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        }
                    });
                });
        }
    };

    private readonly _shouldValidateQuantityLimits = (): boolean => {
        const featureStatuses = this.props.data.featureState.result;
        const isECommerceQuantityLimitsFeatureEnabledInHq =
            featureStatuses?.find(
                featureState => featureState.Name === 'Dynamics.AX.Application.EcommerceQuantityLimitConfigurationsFeature'
            )?.IsEnabled || false;

        if (isECommerceQuantityLimitsFeatureEnabledInHq) {
            return true;
        }

        const isOrderQuantityLimitsFeatureEnabledInHq =
            featureStatuses?.find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature')
                ?.IsEnabled || false;

        if (!isOrderQuantityLimitsFeatureEnabledInHq) {
            return false;
        }

        const useSiteBuilderSettings = featureStatuses?.find(
            featureState => featureState.Name === 'RevertToSiteBuilderOrderQuantityLimitsSettings'
        )?.IsEnabled;

        if (useSiteBuilderSettings) {
            const defaultOrderQuantityLimitsFeatureConfig = this.props.context.request.app.platform?.enableDefaultOrderQuantityLimits;
            const customerInfo = this.props.data.customerInformation.result;

            const isOrderQuantityLimitsFeatureEnabledInSiteSettings =
                defaultOrderQuantityLimitsFeatureConfig === 'all' ||
                (customerInfo &&
                    ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                        (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b)));

            return isOrderQuantityLimitsFeatureEnabledInSiteSettings ?? false;
        } else {
            const cartConfiguration = this.props.data.cartConfiguration?.result;
            return !(cartConfiguration?.IgnoreOrderSettings ?? false);
        }
    };

    private _getMaxQtyMessage(productInventoryInfo: IProductInventoryInformation | undefined, currentQuantity: number): string {
        const { context, data } = this.props;
        const { cart } = data;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- existing code.
        const maxQuantityForLineItem = updateMaxQuantityForCartLineItem(context.app.config.maxQuantityForCartLineItem);

        const addedQuantity =
            cart.result?.cart.CartLines?.find(
                item => item.ItemId?.toString() === this.state.productText && item.ProductId === this.state.productResponse?.RecordId
            )?.Quantity || 0;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- existing code.
        if (
            !productInventoryInfo ||
            !productInventoryInfo.ProductAvailableQuantity.AvailableQuantity ||
            !context.app.config.enableStockCheck
        ) {
            return this._getConcatenatedMaxQtyMessage(addedQuantity.toString(), maxQuantityForLineItem.toString());
        }

        const minInventory = Math.min(maxQuantityForLineItem, productInventoryInfo.ProductAvailableQuantity.AvailableQuantity);

        return this._getConcatenatedMaxQtyMessage(addedQuantity.toString(), minInventory.toString());
    }

    /**
     * Method to return concatenated added quantity Message.
     * @param currentQuantity - Current or added Quantity.
     * @param maxQuantity - Max Quantity.
     * @returns - ConcatenatedMaxQtyMessage.
     */
    private readonly _getConcatenatedMaxQtyMessage = (currentQuantity: string, maxQuantity: string): string => {
        const { quickOrderMaxQuantityLimitText, quickOrderAlreadyItemQuantityText } = this.props.resources;

        if (currentQuantity !== '0') {
            return (
                quickOrderAlreadyItemQuantityText.replace('{curQuantity}', currentQuantity) +
                quickOrderMaxQuantityLimitText.replace('{maxQuantity}', maxQuantity)
            );
        }

        return quickOrderMaxQuantityLimitText.replace('{maxQuantity}', maxQuantity);
    };

    /**
     * Method to handle quantity change.
     * @param value - Quantity.
     */
    private readonly _handleQuantityChange = (value: string): void => {
        let currentValue = Number.parseInt(value, 10);
        const inputElement = this.quantityInput.current instanceof HTMLInputElement && this.quantityInput.current;

        if (isNaN(currentValue)) {
            currentValue = 0;
        }

        this.setState({ quantity: currentValue }, () => {
            debounce(() => {
                if (!isNaN(this.state.quantity) && this.state.quantity >= 0 && inputElement) {
                    inputElement.setAttribute('aria-valuenow', currentValue.toString());
                    inputElement.setAttribute('value', currentValue.toString());
                }
            }, 200)();
        });
    };

    private readonly _updateDimensionValue = (
        productDimensionFull: ProductDimensionFull,
        newValueId: string | undefined
    ): ProductDimensionValue | undefined => {
        if (newValueId && productDimensionFull.DimensionValues) {
            return productDimensionFull.DimensionValues.find(dimension => dimension.RecordId === +newValueId);
        }

        return undefined;
    };

    private readonly _getDropdownName = (dimensionType: number, resources: IProductDimensionConfigureResources): string => {
        const isGiftCard = this.state.productResponse?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    /**
     * Method to update product dimension.
     */
    private readonly _updateDimensions = async (): Promise<void> => {
        const {
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;
        const { productDimensions, productResponse } = this.state;
        if (!productResponse || !productDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({ isUpdatingDimension: true });

        // Step 1: Update state to indicate which dimensions are selected
        if (this.state.productDimensionErrorState.otherError) {
            const clearOtherErrorState = { ...this.state.productDimensionErrorState };
            clearOtherErrorState.otherError = undefined;
            this.setState({ productDimensionErrorState: clearOtherErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            // eslint-disable-next-line security/detect-object-injection
            if (this.state.productDimensionErrorState.configureErrors[key]) {
                const errorState = { ...this.state.productDimensionErrorState };
                // eslint-disable-next-line security/detect-object-injection
                errorState.configureErrors[key] = undefined;

                this.setState({ productDimensionErrorState: errorState });
            }
        }

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions
            .map(dimension => {
                return {
                    DimensionTypeValue: dimension.DimensionTypeValue,
                    DimensionValue:
                        this._updateDimensionValue(dimension, dimensionsToUpdate[dimension.DimensionTypeValue]) || dimension.DimensionValue,
                    ExtensionProperties: dimension.ExtensionProperties
                };
            })
            .filter(dimension => {
                return dimension.DimensionValue;
            });

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProduct = new Promise<SimpleProduct | null>(async (resolve, reject) => {
            const newProduct = await getSelectedVariant(
                new SelectedVariantInput(
                    productResponse.MasterProductId ? productResponse.MasterProductId : productResponse.RecordId,
                    channelId,
                    mappedDimensions,
                    undefined,
                    this.props.context.request
                ),
                actionContext
            );
            if (newProduct) {
                await getDimensionsForSelectedVariant(
                    new GetDimensionsForSelectedVariantInput(
                        newProduct.MasterProductId ? newProduct.MasterProductId : newProduct.RecordId,
                        channelId,
                        mappedDimensions,
                        this.props.context.request
                    ),
                    actionContext
                );
            }

            resolve(newProduct);
        });

        const variantProduct = await selectedProduct;
        this.setState({ productResponse: variantProduct !== null ? variantProduct : undefined });
        if (variantProduct) {
            // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
            // so that places like add to cart can await it
            const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
                new ProductAvailabilitiesForSelectedVariantInput(variantProduct.RecordId, channelId),
                actionContext
            );

            if (newAvailableQuantity && newAvailableQuantity.length > 0) {
                this.setState({ productAvailableQuantity: newAvailableQuantity[0] });
            } else {
                this.setState({ productAvailableQuantity: undefined });
            }
        }
    };

    /**
     * Method to update product availability.
     */
    private readonly _updateAvailability = async (): Promise<void> => {
        const {
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;
        const { productResponse } = this.state;
        if (!productResponse) {
            return;
        }

        let productAvailable: IProductInventoryInformation | undefined;
        await getProductAvailabilitiesForSelectedVariant(
            new ProductAvailabilitiesForSelectedVariantInput(productResponse.RecordId, channelId),
            actionContext
        ).then(result => {
            if (ArrayExtensions.hasElements(result)) {
                productAvailable = result.find(quantity => quantity.ProductAvailableQuantity.ProductId === productResponse.RecordId);
            } else {
                this.setState({ productAvailableQuantity: undefined });
            }
            this.setState({ productAvailableQuantity: productAvailable });
        });
    };

    /**
     * Method to get dimension error string.
     * @param dimensionTypeValue - Dimension value.
     * @param resources - Localized error message.
     * @param isGiftCard - Flag to check giftcard.
     * @returns Dimension error string.
     */
    private readonly _getDimensionErrorString = (
        dimensionTypeValue: number,
        resources: ICipQuickOrderResources,
        isGiftCard?: boolean
    ): string => {
        switch (dimensionTypeValue) {
            case 1: // ProductDimensionType.Color
                return resources.quickOrderproductDimensionTypeColorErrorMessage;
            case 2: // ProductDimensionType.Configuration
                return resources.quickOrderproductDimensionTypeConfigurationErrorMessage;
            case 3: // ProductDimensionType.Size
                return resources.quickOrderproductDimensionTypeSizeErrorMessage;
            case 4: // ProductDimensionType.Style
                return isGiftCard
                    ? resources.quickOrderproductDimensionTypeAmountErrorMessage
                    : resources.quickOrderproductDimensionTypeStyleErrorMessage;
            default:
                return '';
        }
    };

    private readonly _updateProductAvailability = async (): Promise<void> => {
        if (this.state.productAvailableQuantity === undefined) {
            await this._updateAvailability();
        }
    };

    /**
     * Method to validate the cart.
     * @param productResponse - Simple product object.
     * @param quantity - Quantity.
     * @returns Flag after validating cart.
     */
    private readonly _validateCart = async (productResponse: SimpleProduct, quantity: number): Promise<boolean> => {
        const missingDimensions = productResponse.Dimensions?.filter((dimension: ProductDimension) => !dimension.DimensionValue?.Value);
        if (missingDimensions && missingDimensions.length > 0) {
            const userMessage = this._getConfigureErrors(missingDimensions, false);
            if (this.state.productDimensionErrorState.configureErrors) {
                const errorState = { ...this.state.productDimensionErrorState };
                errorState.configureErrors = userMessage;

                this.setState({ productDimensionErrorState: errorState });
                return false;
            }
        }

        if (quantity <= 0) {
            const resources = this.props.resources;
            this.setState({
                stateMessage: {
                    messageText: resources.quickOrderWrongQuantityErrorMsg,
                    statusCssString: 'danger',
                    messageGlyph: 'fas fa-exclamation-triangle',
                    isOpen: true
                }
            });
            return false;
        }

        // If stock check false no need to check the availability.
        const { context } = this.props;
        if (!context.app.config.enableStockCheck) {
            return true;
        }

        await this._updateProductAvailability();
        const { productAvailableQuantity } = this.state;
        const availableQuantity = productAvailableQuantity ? productAvailableQuantity.ProductAvailableQuantity.AvailableQuantity : 0;
        if (availableQuantity === undefined || availableQuantity <= 0) {
            this.setState({
                stateMessage: {
                    messageText: this.props.resources.quickOrderOutofStockErrorMsg,
                    statusCssString: 'danger',
                    messageGlyph: 'fas fa-exclamation-triangle',
                    isOpen: true
                }
            });
            return false;
        }
        return true;
    };

    /**
     * Method to get configuration error string.
     * @param result - List of Dimension values.
     * @param isGiftCard - Flag to check giftcard.
     * @returns Error object.
     */
    private _getConfigureErrors(
        result: ProductDimension[] | undefined,
        isGiftCard?: boolean
    ): { [configureId: string]: string | undefined } {
        if (!result) {
            return {};
        }
        const dimensions: { [configureId: string]: string | undefined } = {};
        const resources = this.props.resources;
        for (const dimension of result) {
            dimensions[dimension.DimensionTypeValue] = this._getDimensionErrorString(dimension.DimensionTypeValue, resources, isGiftCard);
        }
        return dimensions;
    }
}

export default QuickOrder;
